import axios from 'axios';
import Constants from '../config/constants';

const API_BASE_URL = Constants.API_HOST;

// 토큰 가져오기 헬퍼 함수
const getAuthToken = (): string | null => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      return user.accessToken || null;
    }
  } catch (error) {
    console.error('토큰 가져오기 오류:', error);
  }
  return null;
};

export interface PostedContent {
  id: number;
  branchId: number;
  reviewId: number;
  platform: string;
  postId: string;
  content: string;
  images: string[];
  hashtags: string[];
  likes?: number;
  comments?: number;
  shares?: number;
  postUrl?: string;
  isActive: boolean;
  postedAt: string;
  updatedAt: string;
}

export interface PostedContentRequest {
  branchId: number;
  reviewId: number;
  platform: string;
  postId: string;
  content: string;
  images?: string[];
  hashtags?: string[];
}

export interface PostedContentListParams {
  branchId: number;
  platform?: string;
  reviewId?: number;
}

// 백엔드 API 응답 타입
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// 포스팅된 콘텐츠 목록 조회
export const apiGetPostedContents = async (
  params: PostedContentListParams,
): Promise<PostedContent[]> => {
  try {
    const token = getAuthToken();
    const response = await axios.get<ApiResponse<PostedContent[]>>(
      `${API_BASE_URL}/api/posted-contents`,
      {
        params,
        headers: {
          access_token: token,
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error('포스팅된 콘텐츠 조회 API 오류:', error);
    throw error;
  }
};

// 포스팅 기록 생성
export const apiCreatePostedContent = async (
  data: PostedContentRequest,
): Promise<PostedContent> => {
  try {
    const token = getAuthToken();
    const response = await axios.post<ApiResponse<PostedContent>>(
      `${API_BASE_URL}/api/posted-contents`,
      data,
      {
        headers: {
          access_token: token,
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error('포스팅 기록 생성 API 오류:', error);
    throw error;
  }
};

// 포스팅 수정
export const apiUpdatePostedContent = async (
  id: number,
  data: Partial<PostedContentRequest>,
): Promise<PostedContent> => {
  try {
    const token = getAuthToken();
    const response = await axios.put<ApiResponse<PostedContent>>(
      `${API_BASE_URL}/api/posted-contents/${id}`,
      data,
      {
        headers: {
          access_token: token,
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error('포스팅 수정 API 오류:', error);
    throw error;
  }
};

// 포스팅 삭제 (DB + 실제 SNS 포스팅 삭제)
export const apiDeletePostedContent = async (
  id: number,
  deleteSocialMedia: boolean = true,
): Promise<any> => {
  try {
    const token = getAuthToken();
    const response = await axios.delete(
      `${API_BASE_URL}/api/posted-contents/${id}`,
      {
        headers: {
          access_token: token,
        },
        data: {
          deleteSocialMedia,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error('포스팅 삭제 API 오류:', error);
    throw error;
  }
};

// 포스팅 통계 업데이트
export const apiUpdatePostStats = async (
  id: number,
  stats: { likes?: number; comments?: number; shares?: number },
): Promise<PostedContent> => {
  try {
    const token = getAuthToken();
    const response = await axios.put<ApiResponse<PostedContent>>(
      `${API_BASE_URL}/api/posted-contents/${id}/stats`,
      stats,
      {
        headers: {
          access_token: token,
        },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error('포스팅 통계 업데이트 API 오류:', error);
    throw error;
  }
};

// 특정 리뷰의 포스팅 상태 확인
export const apiGetReviewPostingStatus = async (reviewId: number) => {
  try {
    const token = getAuthToken();
    const response = await axios.get<
      ApiResponse<{
        reviewId: number;
        isPosted: boolean;
        platforms: string[];
        postedContents: PostedContent[];
      }>
    >(`${API_BASE_URL}/api/posted-contents/review/${reviewId}/status`, {
      headers: {
        access_token: token,
      },
    });
    return response.data.data;
  } catch (error) {
    console.error('리뷰 포스팅 상태 확인 API 오류:', error);
    throw error;
  }
};

// 소셜 미디어 포스팅 동기화
export const apiSyncPostsFromSocialMedia = async (userId: number) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/facebook-oauth/sync-posts`,
      {
        userId,
      },
    );
    return response.data;
  } catch (error) {
    console.error('포스팅 동기화 API 오류:', error);
    throw error;
  }
};
